import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import {
    addTestCaseError,
    addTestCaseRequest,
    addTestCaseSuccess,
    checkoutTestCaseRevisionError,
    checkoutTestCaseRevisionRequest,
    checkoutTestCaseRevisionSuccess,
    deleteTestCaseError,
    deleteTestCaseRequest,
    deleteTestCaseSuccess,
    duplicateTestCaseError,
    duplicateTestCaseRequest,
    duplicateTestCaseSuccess,
    editTestCaseError,
    editTestCaseRequest,
    editTestCaseSuccess,
    getAllTestCasesError,
    getAllTestCasesRequest,
    getAllTestCasesSuccess,
    getTestCaseByIdError,
    getTestCaseByIdRequest,
    getTestCaseByIdSuccess,
    getTestCaseCertainRevisionError,
    getTestCaseCertainRevisionRequest,
    getTestCaseCertainRevisionSuccess,
    getTestCaseHistoryError,
    getTestCaseHistoryRequest,
    getTestCaseHistorySuccess,
    getTestCaseResultsError,
    getTestCaseResultsRequest,
    getTestCaseResultsSuccess,
    getTestCasesError,
    getTestCasesRequest,
    getTestCasesSuccess,
} from './index';
import {
    addTestCase,
    checkoutTestCaseRevision,
    deleteFileById,
    deleteTestCase,
    editTestCase,
    editTestCaseAttachments,
    getAllTestCases,
    getFileByLink,
    getTestCaseById,
    getTestCaseCertainRevision,
    getTestCaseHistory,
    getTestCaseResults,
    getTestCases,
} from '../api/testсasesApi';
import { selectTestCaseDataActual } from './selectors';
import { addTag } from '@features/tags/api/tagsApi';
import { isEmptyObject } from '@shared/utils/utilsOld';

export function* getTestCasesSaga(action) {
    const { enableInfiniteScroll, ...params } = action.payload;
    try {
        const response = yield call(getTestCases, params);
        yield put(getTestCasesSuccess({ ...response, enableInfiniteScroll: Boolean(enableInfiniteScroll) }));
    } catch (error) {
        yield put(getTestCasesError(error));
    }
}

export function* getAllTestCasesSaga(action) {
    const { module, fields } = action.payload;

    try {
        const response = yield call(getAllTestCases, module, fields);
        yield put(getAllTestCasesSuccess(response));
    } catch (error) {
        yield put(getAllTestCasesError(error));
    }
}

export function* getTestCaseHistorySaga(action) {
    try {
        let response = yield call(getTestCaseHistory, action.payload);
        yield put(getTestCaseHistorySuccess(response));
    } catch (error) {
        yield put(getTestCaseHistoryError(error));
    }
}

export function* getTestCaseCertainRevisionSaga(action) {
    try {
        let response = yield call(getTestCaseCertainRevision, action.payload);
        yield put(getTestCaseCertainRevisionSuccess(response));
    } catch (error) {
        yield put(getTestCaseCertainRevisionError(error));
    }
}

export function* getTestCaseByIdSaga(action) {
    try {
        let response = yield call(getTestCaseById, action.payload);

        response.data.attachments = yield call(getFilesSaga, response.data.attachments);
        yield put(getTestCaseByIdSuccess(response));
    } catch (error) {
        yield put(getTestCaseByIdError(error));
    }
}

export function* getTestCaseResultsSaga(action) {
    const { id, ...restParams } = action.payload;

    try {
        const response = yield call(getTestCaseResults, id, restParams);
        yield put(getTestCaseResultsSuccess(response));
    } catch (error) {
        yield put(getTestCaseResultsError(error));
    }
}

export function* checkoutTestCaseRevisionSaga(action) {
    const { testcaseId, revisionId } = action.payload;

    try {
        yield call(checkoutTestCaseRevision, testcaseId, revisionId);
        yield put(checkoutTestCaseRevisionSuccess());

        // fetch new testcase version and update revisions list
        yield put(getTestCaseHistoryRequest(testcaseId));
    } catch (error) {
        yield put(checkoutTestCaseRevisionError(error));
    } finally {
        // anyway fetch current version of testcase
        yield put(getTestCaseByIdRequest(testcaseId));
    }
}

export function* getFilesSaga(fileObjects) {
    let files = [];
    while (fileObjects.length !== 0) {
        const { pk, file } = fileObjects.shift(0);
        let response;
        try {
            response = yield call(getFileByLink, file);
        } catch (error) {
            console.error('File fetching failed due to error:', error.message);
        }
        if (response) {
            const url = response.config.url;
            const fileName = url.slice(url.lastIndexOf('/') + 1);
            const fileObject = new File([response.data], fileName);
            fileObject.fileLink = file;
            fileObject.id = pk;
            files.push(fileObject);
        }
    }

    return files;
}

export function* deleteFileSaga(file) {
    try {
        yield call(deleteFileById, file.id);
    } catch (error) {
        console.error('File deletion failed due to error: ', error.message);
    }
}

export function* addTestCaseSaga(action) {
    const testCaseData = structuredClone(action.payload);

    try {
        if (Array.isArray(testCaseData.tags)) {
            for (const tag of testCaseData.tags) {
                if (tag.wasAdded === undefined) continue;
                yield call(addTag, tag.text, 'testcases');
            }
        }

        const attachments = testCaseData.attachments;
        delete testCaseData.attachments; // first request must be without attachments

        const { data } = yield call(addTestCase, testCaseData);

        if (attachments?.length) {
            yield call(editTestCaseAttachments, data.pk, attachments);
        }

        yield put(addTestCaseSuccess(data));
    } catch (error) {
        yield put(addTestCaseError(error));
    }
}

export function* editTestCaseSaga(action) {
    const fetchedTestcase = yield select(selectTestCaseDataActual);
    const { id, params } = action.payload;
    const { attachments, ...resultRequestParams } = params;

    let attachmentsToAdd;

    if (attachments) {
        // delete existing files if they were deleted in ui
        for (const attachment of fetchedTestcase.attachments) {
            const sameFiles = attachments.filter(
                (att) => att.name === attachment.name && att.lastModified === attachment.lastModified,
            );

            if (!sameFiles.length) {
                yield call(deleteFileSaga, attachment);
            }
        }
        // put only new files to request params
        let resultAttachments = [];
        attachments.forEach((attachment) => {
            const sameFiles = fetchedTestcase.attachments.filter(
                (att) => att.name === attachment.name && att.lastModified === attachment.lastModified,
            );
            if (!sameFiles.length) {
                resultAttachments.push(attachment);
            }
        });
        if (resultAttachments.length) {
            attachmentsToAdd = resultAttachments;
        }
    }

    try {
        let dataToUpdateLocalState;

        if (Array.isArray(resultRequestParams.tags)) {
            for (const tag of resultRequestParams.tags) {
                if (tag.wasAdded === undefined) continue;
                yield call(addTag, tag.text, 'testcases');
            }
        }

        if (!isEmptyObject(resultRequestParams)) {
            const { data } = yield call(editTestCase, id, resultRequestParams);
            dataToUpdateLocalState = data;
        }

        if (attachmentsToAdd?.length) {
            const { data } = yield call(editTestCaseAttachments, id, attachmentsToAdd);
            dataToUpdateLocalState = data;
        }

        yield put(editTestCaseSuccess(dataToUpdateLocalState));
        yield put(getTestCaseByIdRequest(id));
    } catch (error) {
        yield put(editTestCaseError(error));
    }
}

export function* deleteTestCaseSaga(action) {
    const { id } = action.payload;

    try {
        const response = yield call(deleteTestCase, id);
        yield put(deleteTestCaseSuccess(response));
    } catch (error) {
        yield put(deleteTestCaseError(error));
    }
}

export function* duplicateTestCaseSaga(action) {
    const testCaseToDuplicateId = action.payload.id;
    const newTestCaseName = action.payload.name;

    try {
        const { data: testCaseToDuplicateData } = yield call(getTestCaseById, testCaseToDuplicateId);
        const clonedTestcaseData = structuredClone(testCaseToDuplicateData);
        clonedTestcaseData.name = newTestCaseName;

        let attachments = structuredClone(clonedTestcaseData.attachments);
        delete clonedTestcaseData.createdAt;
        delete clonedTestcaseData.attachments; // first request must be without attachments

        const { data } = yield call(addTestCase, clonedTestcaseData);

        if (attachments?.length) {
            attachments = yield call(getFilesSaga, attachments);
            yield call(editTestCaseAttachments, data.pk, attachments);
        }

        yield put(duplicateTestCaseSuccess());
    } catch (error) {
        yield put(duplicateTestCaseError(error));
    }
}

export default function* testCasesSagaWatcher() {
    yield all([
        takeLatest(getTestCasesRequest, getTestCasesSaga),
        takeLatest(getAllTestCasesRequest, getAllTestCasesSaga),
        takeLatest(getTestCaseByIdRequest, getTestCaseByIdSaga),
        takeLatest(getTestCaseResultsRequest, getTestCaseResultsSaga),
        takeLatest(getTestCaseHistoryRequest, getTestCaseHistorySaga),
        takeLatest(addTestCaseRequest, addTestCaseSaga),
        takeLatest(editTestCaseRequest, editTestCaseSaga),
        takeLatest(deleteTestCaseRequest, deleteTestCaseSaga),
        takeLatest(getTestCaseCertainRevisionRequest, getTestCaseCertainRevisionSaga),
        takeLatest(checkoutTestCaseRevisionRequest, checkoutTestCaseRevisionSaga),
        takeLatest(duplicateTestCaseRequest, duplicateTestCaseSaga),
    ]);
}
